// 류 주(注)·부 주의 "제외" 조항을 사람이 읽을 수 있게 뽑아 낸다.
// 범용 류 트리의 "제N류 주에 따른 제외 물품입니까?" 질문은 조항을 모르면 답할 수 없으므로
// 카드가 이 조항을 질문 아래에 펼쳐 보인다. 원문은 기존 notes-search(CLIP 류주/부주)에서 온다.

const NOTE_TYPES = [
  { noteType: "hstdManl", kind: "chapter" },
  { noteType: "sctManl", kind: "section" },
];

export function stripNoteHtml(text) {
  return String(text || "")
    .replace(/<a\b[^>]*>/gi, "")
    .replace(/<\/a>/gi, "")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/[ \t]+/g, " ");
}

const EXCLUSION_HEAD = /(제외한다|제외되는|분류하지 않는다|포함하지 않는다)/;
const NUMBERED = /^\d+\.\s/;
const LETTERED = /^[가-힣]\.\s|^\([가-힣0-9]+\)\s|^[a-z]\)\s/;

/**
 * 주 원문에서 제외 조항 블록을 뽑는다. "N. … 제외한다." 문장과 그에 딸린 "가./나./다." 항목을
 * 한 블록으로 묶고, 그 밖에 "제외한다"가 든 문장은 단독 항목으로 넣는다.
 * 반환: [{ title, items: [] }] — title은 조항 문장, items는 하위 목.
 */
export function extractExclusionClauses(noteText, limit = 80) {
  const lines = stripNoteHtml(noteText)
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const clauses = [];
  let current = null;
  let count = 0;
  const stop = () => count >= limit;
  let remaining = 0;
  for (let li = 0; li < lines.length; li += 1) {
    const line = lines[li];
    if (stop()) { remaining = lines.length - li; break; }
    // 총설·해설이 시작되면 주(注) 본문이 끝난 것이다.
    if (/^(총설|해설|General|호의 해설)/.test(line) && clauses.length) break;
    if (NUMBERED.test(line) || /^주:/.test(line)) {
      current = null;
      if (EXCLUSION_HEAD.test(line) && /다음 각 목|다음의 것|다음 물품/.test(line)) {
        current = { title: line, items: [] };
        clauses.push(current);
        count += 1;
      } else if (EXCLUSION_HEAD.test(line)) {
        clauses.push({ title: line, items: [] });
        count += 1;
      }
      continue;
    }
    if (current && LETTERED.test(line)) {
      current.items.push(line);
      count += 1;
      continue;
    }
    if (current && !LETTERED.test(line)) {
      // 목 나열이 끝나면 블록을 닫는다. 단, 이어지는 설명 문장은 마지막 목에 붙이지 않는다.
      current = null;
    }
    if (EXCLUSION_HEAD.test(line) && !/^[가-힣]\.\s/.test(line)) {
      clauses.push({ title: line, items: [] });
      count += 1;
    }
  }
  // 한도에서 끊겼으면 잘렸다는 표식과 남은 줄 수(화면이 '일부만 보임 · 남은 N줄'을 사실대로 밝힌다, 2026-09-25 검수 v4).
  if (stop() && remaining > 0) {
    clauses.truncated = true;
    clauses.remainingLines = remaining;
  }
  return clauses;
}

/**
 * 류 주와 부 주의 제외 조항을 가져온다. hs4는 그 류의 임의 호면 되고(없으면 `NN01`),
 * 결과: { chapter: {label, clauses}, section: {label, clauses} } (없으면 null).
 */
export async function fetchExclusionNotes({ chapter, hs4, fetchImpl = fetch, signal } = {}) {
  const digits = String(chapter || "").replace(/\D/g, "").padStart(2, "0");
  if (!digits || digits === "00") return { chapter: null, section: null };
  const heading = /^\d{4}$/.test(String(hs4 || "")) ? String(hs4) : `${digits}01`;
  const out = { chapter: null, section: null };
  await Promise.all(NOTE_TYPES.map(async ({ noteType, kind }) => {
    try {
      const params = new URLSearchParams({ q: "", hs4: heading, lang: "kor", limit: "1", note_type: noteType, full: "1" });
      const response = await fetchImpl(`/api/onto/notes-search?${params.toString()}`, { signal });
      if (!response.ok) { out.failed = true; return; }
      const data = await response.json();
      const item = Array.isArray(data?.items) ? data.items[0] : null;
      if (!item) return;
      out[kind] = {
        label: stripNoteHtml(item.label || "").trim(),
        clauses: extractExclusionClauses(item.desc || item.text || ""),
      };
    } catch {
      /* 조항 표시는 보조 정보다. 실패해도 질문은 그대로 남긴다 — 다만 '찾지 못함'과 '불러오지 못함'을 구분해 알린다. */
      out.failed = true;
    }
  }));
  return out;
}
